import { parseAddressImportFile } from '../../utils/parse-address-import';

type MobileConfigApiResponse = {
  ios: { minVersion: string; forceUpdate: boolean };
  android: { minVersion: string; forceUpdate: boolean };
};

import type {
  Address,
  AddressDraft,
  AddressImportRow,
  AddressReview,
  AdminAccount,
  AdminDraft,
  Appointment,
  AppointmentDraft,
  AppointmentReasonDraft,
  AppointmentReasonRef,
  AppUser,
  AppUserDraft,
  Article,
  ArticleCategoryDraft,
  ArticleCategoryRef,
  ArticleDraft,
  AuthenticatedAdmin,
  BusinessTypeDraft,
  BusinessTypeRef,
  City,
  Company,
  CompanyDraft,
  DirectoryCategoryDraft,
  DirectoryCategoryRef,
  OfferCategoryDraft,
  OfferCategoryRef,
  CompanyPerformance,
  Country,
  CountryDraft,
  FlaggedReview,
  Formula,
  FormulaDraft,
  NotificationLogEntry,
  NotificationTemplates,
  Offer,
  OfferDraft,
  OfferTransaction,
  PerformanceTotals,
  PregnancyWeek,
  ReminderSettings,
  MobileConfig,
} from '@domain/entities';
import type {
  AddressQuery,
  AddressRepository,
  AdminRepository,
  AppointmentQuery,
  AppointmentRepository,
  AppointmentReasonRepository,
  AppUserQuery,
  AppUserRepository,
  ArticleCategoryRepository,
  ArticleQuery,
  ArticleRepository,
  AuditLogRepository,
  AuthRepository,
  BusinessTypeRepository,
  CompanyCreateInput,
  CompanyQuery,
  CompanyRepository,
  DirectoryCategoryRepository,
  OfferCategoryRepository,
  CityRepository,
  CountryRepository,
  FormulaRepository,
  NotificationRepository,
  OfferCreateInput,
  OfferQuery,
  OfferRepository,
  PerformanceQuery,
  PerformanceRepository,
  PregnancyRepository,
  ReviewRepository,
  MobileConfigRepository,
} from '@domain/repositories';
import type { AuditLogEntry, AuditLogPage, AuditLogQuery } from '@domain/entities';
import type {
  AccountStatus,
  ModerationStatus,
  NotificationChannel,
  NotificationType,
  OfferStatus,
  Page,
  PublicationStatus,
} from '@domain/value-objects';
import type {
  AddressDto,
  AddressReviewDto,
  AdminDto,
  AppointmentDto,
  AppointmentReasonDto,
  AppUserDto,
  ArticleCategoryDto,
  ArticleDto,
  BusinessTypeDto,
  CityDto,
  CompanyDto,
  CountryDto,
  DirectoryCategoryDto,
  FormulaDto,
  NotificationLogDto,
  NotificationTemplateDto,
  OfferCategoryDto,
  OfferDto,
  PageDto,
  PerformanceRowDto,
  PerformanceTotalsDto,
  PregnancyWeekDto,
  ReviewDto,
  SessionDto,
  TransactionDto,
} from '../../dto';
import * as map from '../../mappers';
import type { HttpClient } from '../../http/http-client';

const TOKEN_KEY = 'babimams.token';

export class HttpAuthRepository implements AuthRepository {
  constructor(private readonly http: HttpClient) {}

  async signIn(email: string, password: string): Promise<AuthenticatedAdmin> {
    const dto = await this.http.post<SessionDto>('/admin/auth/login', { email, password });
    sessionStorage.setItem(TOKEN_KEY, dto.accessToken);
    return map.toAuthenticatedAdmin(dto);
  }

  async signOut(): Promise<void> {
    try {
      await this.http.post<void>('/admin/auth/logout');
    } finally {
      sessionStorage.removeItem(TOKEN_KEY);
    }
  }

  async restoreSession(): Promise<AuthenticatedAdmin | null> {
    if (!sessionStorage.getItem(TOKEN_KEY)) return null;
    const dto = await this.http.get<SessionDto>('/admin/auth/me');
    return map.toAuthenticatedAdmin(dto);
  }
}

export function readAccessToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function clearAccessToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

export class HttpAppointmentRepository implements AppointmentRepository {
  constructor(private readonly http: HttpClient) {}

  async search(query: AppointmentQuery): Promise<Page<Appointment>> {
    const dto = await this.http.get<PageDto<AppointmentDto>>('/admin/appointments', { query: { ...query } });
    return map.toPage(dto, map.toAppointment);
  }

  async create(draft: AppointmentDraft): Promise<Appointment> {
    const dto = await this.http.post<AppointmentDto>('/admin/appointments', draft);
    return map.toAppointment(dto);
  }

  async update(id: string, draft: Omit<AppointmentDraft, 'userId'>): Promise<Appointment> {
    const dto = await this.http.patch<AppointmentDto>(`/admin/appointments/${id}`, draft);
    return map.toAppointment(dto);
  }

  async remove(id: string): Promise<void> {
    await this.http.delete<{ ok: true }>(`/admin/appointments/${id}`);
  }
}

export class HttpCompanyRepository implements CompanyRepository {
  constructor(private readonly http: HttpClient) {}

  async create(input: CompanyCreateInput): Promise<Company> {
    return map.toCompany(await this.http.post<CompanyDto>('/admin/businesses', input));
  }

  async search(query: CompanyQuery): Promise<Page<Company>> {
    const dto = await this.http.get<PageDto<CompanyDto>>('/admin/businesses', { query: { ...query } });
    return map.toPage(dto, map.toCompany);
  }

  async findById(id: string): Promise<Company> {
    return map.toCompany(await this.http.get<CompanyDto>(`/admin/businesses/${id}`));
  }

  async countByStatus(status: ModerationStatus): Promise<number> {
    const dto = await this.http.get<{ count: number }>('/admin/businesses/count', { query: { status } });
    return dto.count;
  }

  async listByStatus(status: ModerationStatus): Promise<Company[]> {
    const dto = await this.http.get<CompanyDto[]>('/admin/businesses', { query: { status, all: true } });
    return dto.map(map.toCompany);
  }

  async update(id: string, draft: CompanyDraft): Promise<Company> {
    return map.toCompany(await this.http.patch<CompanyDto>(`/admin/businesses/${id}`, draft));
  }

  async setStatus(ids: readonly string[], status: ModerationStatus): Promise<Company[]> {
    const dto = await this.http.post<CompanyDto[]>('/admin/businesses/moderate', { ids, status });
    return dto.map(map.toCompany);
  }

  async remove(id: string): Promise<void> {
    await this.http.delete(`/admin/businesses/${id}`);
  }
}

export class HttpOfferRepository implements OfferRepository {
  constructor(private readonly http: HttpClient) {}

  async create(input: OfferCreateInput): Promise<Offer> {
    return map.toOffer(await this.http.post<OfferDto>('/admin/offers', input));
  }

  async search(query: OfferQuery): Promise<Page<Offer>> {
    const dto = await this.http.get<PageDto<OfferDto>>('/admin/offers', { query: { ...query } });
    return map.toPage(dto, map.toOffer);
  }

  async findById(id: string): Promise<Offer> {
    return map.toOffer(await this.http.get<OfferDto>(`/admin/offers/${id}`));
  }

  async countByStatus(status: OfferStatus): Promise<number> {
    const dto = await this.http.get<{ count: number }>('/admin/offers/count', { query: { status } });
    return dto.count;
  }

  async listByStatus(status: OfferStatus): Promise<Offer[]> {
    const dto = await this.http.get<OfferDto[]>('/admin/offers', { query: { status, all: true } });
    return dto.map(map.toOffer);
  }

  async topByClicks(limit: number): Promise<Offer[]> {
    const dto = await this.http.get<OfferDto[]>('/admin/offers/top', { query: { limit } });
    return dto.map(map.toOffer);
  }

  async update(id: string, draft: OfferDraft): Promise<Offer> {
    return map.toOffer(await this.http.patch<OfferDto>(`/admin/offers/${id}`, draft));
  }

  async setStatus(ids: readonly string[], status: OfferStatus): Promise<Offer[]> {
    const dto = await this.http.post<OfferDto[]>('/admin/offers/moderate', { ids, status });
    return dto.map(map.toOffer);
  }

  async remove(id: string): Promise<void> {
    await this.http.delete(`/admin/offers/${id}`);
  }
}

export class HttpArticleRepository implements ArticleRepository {
  constructor(private readonly http: HttpClient) {}

  async search(query: ArticleQuery): Promise<Page<Article>> {
    const dto = await this.http.get<PageDto<ArticleDto>>('/admin/articles', { query: { ...query } });
    return map.toPage(dto, map.toArticle);
  }

  async findById(id: string): Promise<Article> {
    return map.toArticle(await this.http.get<ArticleDto>(`/admin/articles/${id}`));
  }

  async countByStatus(status: PublicationStatus): Promise<number> {
    const dto = await this.http.get<{ count: number }>('/admin/articles/count', { query: { status } });
    return dto.count;
  }

  async create(draft: ArticleDraft): Promise<Article> {
    const { imageKeys, ...rest } = draft;
    const body = {
      ...rest,
      coverKey: imageKeys?.[0] ?? undefined,
      images: imageKeys ? imageKeys.slice(1) : [],
    };
    return map.toArticle(await this.http.post<ArticleDto>('/admin/articles', body));
  }

  async update(id: string, draft: ArticleDraft): Promise<Article> {
    const { imageKeys, ...rest } = draft;
    const body: Record<string, unknown> = { ...rest };
    if (imageKeys !== undefined) {
      body['coverKey'] = imageKeys[0] ?? null;
      body['images'] = imageKeys.slice(1);
    }
    return map.toArticle(await this.http.patch<ArticleDto>(`/admin/articles/${id}`, body));
  }

  async setStatus(ids: readonly string[], status: PublicationStatus): Promise<Article[]> {
    const dto = await this.http.post<ArticleDto[]>('/admin/articles/status', { ids, status });
    return dto.map(map.toArticle);
  }

  async remove(id: string): Promise<void> {
    await this.http.delete<void>(`/admin/articles/${id}`);
  }
}

export class HttpAddressRepository implements AddressRepository {
  constructor(private readonly http: HttpClient) {}

  async search(query: AddressQuery): Promise<Page<Address>> {
    const dto = await this.http.get<PageDto<AddressDto>>('/admin/directory-entries', { query: { ...query } });
    return map.toPage(dto, map.toAddress);
  }

  async create(draft: AddressDraft): Promise<Address> {
    return map.toAddress(await this.http.post<AddressDto>('/admin/directory-entries', draft));
  }

  async update(id: string, draft: AddressDraft): Promise<Address> {
    return map.toAddress(await this.http.patch<AddressDto>(`/admin/directory-entries/${id}`, draft));
  }

  async remove(ids: readonly string[]): Promise<void> {
    await this.http.post<void>('/admin/directory-entries/bulk-delete', { ids });
  }

  async setActive(ids: readonly string[], active: boolean): Promise<Address[]> {
    const publishedAt = active ? new Date().toISOString() : null;
    const results = await Promise.all(
      ids.map((id) =>
        this.http.patch<AddressDto>(`/admin/directory-entries/${id}`, { publishedAt }),
      ),
    );
    return results.map(map.toAddress);
  }

  async listReviews(addressId: string): Promise<AddressReview[]> {
    const dto = await this.http.get<AddressReviewDto[]>(`/admin/directory-entries/${addressId}/reviews`);
    return dto.map(map.toAddressReview);
  }

  async deleteReview(reviewId: string): Promise<void> {
    await this.http.delete<void>(`/admin/reviews/${reviewId}`);
  }

  /** Parse le fichier Excel/CSV dans le navigateur et retourne les lignes prévisualisées. */
  async parseImportFile(file: File): Promise<AddressImportRow[]> {
    return parseAddressImportFile(file);
  }

  async importRows(rows: readonly AddressImportRow[]): Promise<Address[]> {
    const dto = await this.http.post<AddressDto[]>('/admin/directory-entries/import', { rows });
    return dto.map(map.toAddress);
  }
}

export class HttpReviewRepository implements ReviewRepository {
  constructor(private readonly http: HttpClient) {}

  async listFlagged(): Promise<FlaggedReview[]> {
    const dto = await this.http.get<ReviewDto[]>('/admin/reviews/flagged');
    return dto.map(map.toReview);
  }

  async countFlagged(): Promise<number> {
    const dto = await this.http.get<{ count: number }>('/admin/reviews/flagged/count');
    return dto.count;
  }

  async dismiss(id: string): Promise<void> {
    await this.http.post<void>(`/admin/reviews/${id}/dismiss`);
  }

  async remove(id: string): Promise<void> {
    await this.http.delete<void>(`/admin/reviews/${id}`);
  }
}

export class HttpAppUserRepository implements AppUserRepository {
  constructor(private readonly http: HttpClient) {}

  async search(query: AppUserQuery): Promise<Page<AppUser>> {
    const dto = await this.http.get<PageDto<AppUserDto>>('/admin/users', { query: { ...query } });
    return map.toPage(dto, map.toAppUser);
  }

  async findById(id: string): Promise<AppUser> {
    return map.toAppUser(await this.http.get<AppUserDto>(`/admin/users/${id}`));
  }

  async countByStatus(status: AccountStatus): Promise<number> {
    const dto = await this.http.get<{ count: number }>('/admin/users/count', { query: { status } });
    return dto.count;
  }

  async create(draft: AppUserDraft): Promise<AppUser> {
    return map.toAppUser(await this.http.post<AppUserDto>('/admin/users', draft));
  }

  async update(id: string, draft: AppUserDraft): Promise<AppUser> {
    return map.toAppUser(await this.http.patch<AppUserDto>(`/admin/users/${id}`, draft));
  }

  async setStatus(id: string, status: AccountStatus): Promise<AppUser> {
    return map.toAppUser(await this.http.patch<AppUserDto>(`/admin/users/${id}/status`, { status }));
  }

  async exportAll(): Promise<AppUser[]> {
    const dto = await this.http.get<AppUserDto[]>('/admin/users/export');
    return dto.map(map.toAppUser);
  }

  async remove(id: string): Promise<void> {
    await this.http.delete(`/admin/users/${id}`);
  }
}

export class HttpAdminRepository implements AdminRepository {
  constructor(private readonly http: HttpClient) {}

  async list(): Promise<AdminAccount[]> {
    const dto = await this.http.get<AdminDto[]>('/admin/admins');
    return dto.map(map.toAdmin);
  }

  async findById(id: string): Promise<AdminAccount> {
    return map.toAdmin(await this.http.get<AdminDto>(`/admin/admins/${id}`));
  }

  async create(draft: AdminDraft): Promise<AdminAccount> {
    const body = { ...draft, password: draft.password || undefined };
    return map.toAdmin(await this.http.post<AdminDto>('/admin/admins', body));
  }

  async update(id: string, draft: AdminDraft): Promise<AdminAccount> {
    const body = { ...draft, password: draft.password || undefined };
    return map.toAdmin(await this.http.patch<AdminDto>(`/admin/admins/${id}`, body));
  }

  async setStatus(id: string, status: AccountStatus): Promise<AdminAccount> {
    return map.toAdmin(await this.http.patch<AdminDto>(`/admin/admins/${id}/status`, { status }));
  }

  async toggleRole(id: string): Promise<AdminAccount> {
    return map.toAdmin(await this.http.post<AdminDto>(`/admin/admins/${id}/toggle-role`));
  }
}

export class HttpFormulaRepository implements FormulaRepository {
  constructor(private readonly http: HttpClient) {}

  async list(): Promise<Formula[]> {
    const dto = await this.http.get<FormulaDto[]>('/admin/formulas');
    return dto.map(map.toFormula);
  }

  async create(draft: FormulaDraft): Promise<Formula> {
    return map.toFormula(await this.http.post<FormulaDto>('/admin/formulas', draft));
  }

  async update(id: string, draft: FormulaDraft): Promise<Formula> {
    return map.toFormula(await this.http.patch<FormulaDto>(`/admin/formulas/${id}`, draft));
  }

  async remove(id: string): Promise<void> {
    await this.http.delete<void>(`/admin/formulas/${id}`);
  }

  async getReminder(): Promise<ReminderSettings> {
    return this.http.get<ReminderSettings>('/admin/formulas/reminder');
  }

  async saveReminder(settings: ReminderSettings): Promise<ReminderSettings> {
    return this.http.put<ReminderSettings>('/admin/formulas/reminder', settings);
  }
}

export class HttpCountryRepository implements CountryRepository {
  constructor(private readonly http: HttpClient) {}

  async list(): Promise<Country[]> {
    const dto = await this.http.get<CountryDto[]>('/admin/countries');
    return dto.map(map.toCountry);
  }

  async create(draft: CountryDraft): Promise<Country> {
    return map.toCountry(await this.http.post<CountryDto>('/admin/countries', draft));
  }

  async update(id: string, draft: CountryDraft): Promise<Country> {
    return map.toCountry(await this.http.patch<CountryDto>(`/admin/countries/${id}`, draft));
  }

  async toggleActive(id: string): Promise<Country> {
    return map.toCountry(await this.http.post<CountryDto>(`/admin/countries/${id}/toggle`));
  }

  async remove(id: string): Promise<void> {
    await this.http.delete<void>(`/admin/countries/${id}`);
  }
}

export class HttpPregnancyRepository implements PregnancyRepository {
  constructor(private readonly http: HttpClient) {}

  async list(search: string): Promise<PregnancyWeek[]> {
    const dto = await this.http.get<PregnancyWeekDto[]>('/admin/pregnancy-weeks', { query: { search } });
    return dto.map(map.toPregnancyWeek);
  }

  async get(week: number): Promise<PregnancyWeek> {
    return map.toPregnancyWeek(
      await this.http.get<PregnancyWeekDto>(`/admin/pregnancy-weeks/${week}`),
    );
  }

  async save(week: number, draft: import('@domain/entities').PregnancyWeekDraft): Promise<PregnancyWeek> {
    return map.toPregnancyWeek(
      await this.http.post<PregnancyWeekDto>('/admin/pregnancy-weeks', { weekNumber: week, ...draft }),
    );
  }

  async validate(week: number): Promise<PregnancyWeek> {
    return map.toPregnancyWeek(
      await this.http.post<PregnancyWeekDto>(`/admin/pregnancy-weeks/${week}/validate`),
    );
  }
}

export class HttpNotificationRepository implements NotificationRepository {
  constructor(private readonly http: HttpClient) {}

  async getTemplates(): Promise<NotificationTemplates> {
    const dto = await this.http.get<Record<NotificationType, NotificationTemplateDto>>(
      '/admin/notifications/templates',
    );
    return {
      offerApproved: map.toNotificationTemplate(dto.offerApproved),
      offerRejected: map.toNotificationTemplate(dto.offerRejected),
      articlePublished: map.toNotificationTemplate(dto.articlePublished),
    };
  }

  async saveTemplate(
    type: NotificationType,
    patch: Partial<{ subject: string; body: string; channel: NotificationChannel }>,
  ): Promise<NotificationTemplates> {
    await this.http.patch<void>(`/admin/notifications/templates/${type}`, patch);
    return this.getTemplates();
  }

  async listLog(limit: number): Promise<NotificationLogEntry[]> {
    const dto = await this.http.get<NotificationLogDto[]>('/admin/notifications/log', {
      query: { limit },
    });
    return dto.map(map.toNotificationLog);
  }

  async append(
    type: NotificationType,
    company: string,
    subject: string,
  ): Promise<NotificationLogEntry> {
    const dto = await this.http.post<NotificationLogDto>('/admin/notifications/log', {
      type,
      companyName: company,
      subject,
    });
    return map.toNotificationLog(dto);
  }
}

export class HttpPerformanceRepository implements PerformanceRepository {
  constructor(private readonly http: HttpClient) {}

  async search(query: PerformanceQuery): Promise<Page<CompanyPerformance>> {
    const dto = await this.http.get<PageDto<PerformanceRowDto>>('/admin/performance/companies', {
      query: { ...query },
    });
    return map.toPage(dto, map.toPerformanceRow);
  }

  async totals(): Promise<PerformanceTotals> {
    return map.toPerformanceTotals(
      await this.http.get<PerformanceTotalsDto>('/admin/performance/totals'),
    );
  }

  async journal(search: string, limit: number): Promise<OfferTransaction[]> {
    const dto = await this.http.get<TransactionDto[]>('/admin/performance/journal', {
      query: { search, limit },
    });
    return dto.map(map.toTransaction);
  }
}

export class HttpArticleCategoryRepository implements ArticleCategoryRepository {
  constructor(private readonly http: HttpClient) {}

  async list(): Promise<ArticleCategoryRef[]> {
    const dto = await this.http.get<ArticleCategoryDto[]>('/admin/article-categories');
    return dto.map((d) => ({ id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive }));
  }

  async create(draft: ArticleCategoryDraft): Promise<ArticleCategoryRef> {
    const d = await this.http.post<ArticleCategoryDto>('/admin/article-categories', {
      slug: draft.slug,
      label: draft.label,
      sortOrder: draft.sortOrder,
      isActive: true,
    });
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async update(id: string, draft: Partial<ArticleCategoryDraft>): Promise<ArticleCategoryRef> {
    const d = await this.http.patch<ArticleCategoryDto>(`/admin/article-categories/${id}`, draft);
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async toggleActive(id: string, currentIsActive: boolean): Promise<ArticleCategoryRef> {
    const d = await this.http.patch<ArticleCategoryDto>(`/admin/article-categories/${id}`, {
      isActive: !currentIsActive,
    });
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async remove(id: string): Promise<void> {
    await this.http.delete<void>(`/admin/article-categories/${id}`);
  }
}

export class HttpAppointmentReasonRepository implements AppointmentReasonRepository {
  constructor(private readonly http: HttpClient) {}

  async list(): Promise<AppointmentReasonRef[]> {
    const dto = await this.http.get<AppointmentReasonDto[]>('/admin/appointment-reasons');
    return dto.map((d) => ({ id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive }));
  }

  async create(draft: AppointmentReasonDraft): Promise<AppointmentReasonRef> {
    const d = await this.http.post<AppointmentReasonDto>('/admin/appointment-reasons', {
      slug: draft.slug, label: draft.label, sortOrder: draft.sortOrder, isActive: true,
    });
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async update(id: string, draft: Partial<AppointmentReasonDraft>): Promise<AppointmentReasonRef> {
    const d = await this.http.patch<AppointmentReasonDto>(`/admin/appointment-reasons/${id}`, draft);
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async toggleActive(id: string, currentIsActive: boolean): Promise<AppointmentReasonRef> {
    const d = await this.http.patch<AppointmentReasonDto>(`/admin/appointment-reasons/${id}`, { isActive: !currentIsActive });
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async remove(id: string): Promise<void> {
    await this.http.delete<void>(`/admin/appointment-reasons/${id}`);
  }
}

export class HttpDirectoryCategoryRepository implements DirectoryCategoryRepository {
  constructor(private readonly http: HttpClient) {}

  async list(): Promise<DirectoryCategoryRef[]> {
    const dto = await this.http.get<DirectoryCategoryDto[]>('/admin/directory-categories');
    return dto.map((d) => ({ id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive }));
  }

  async create(draft: DirectoryCategoryDraft): Promise<DirectoryCategoryRef> {
    const d = await this.http.post<DirectoryCategoryDto>('/admin/directory-categories', {
      slug: draft.slug, label: draft.label, sortOrder: draft.sortOrder, isActive: true,
    });
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async update(id: string, draft: Partial<DirectoryCategoryDraft>): Promise<DirectoryCategoryRef> {
    const d = await this.http.patch<DirectoryCategoryDto>(`/admin/directory-categories/${id}`, draft);
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async toggleActive(id: string, currentIsActive: boolean): Promise<DirectoryCategoryRef> {
    const d = await this.http.patch<DirectoryCategoryDto>(`/admin/directory-categories/${id}`, { isActive: !currentIsActive });
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async remove(id: string): Promise<void> {
    await this.http.delete<void>(`/admin/directory-categories/${id}`);
  }
}

export class HttpBusinessTypeRepository implements BusinessTypeRepository {
  constructor(private readonly http: HttpClient) {}

  async list(): Promise<BusinessTypeRef[]> {
    const dto = await this.http.get<BusinessTypeDto[]>('/admin/business-types');
    return dto.map((d) => ({ id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive }));
  }

  async create(draft: BusinessTypeDraft): Promise<BusinessTypeRef> {
    const d = await this.http.post<BusinessTypeDto>('/admin/business-types', {
      slug: draft.slug, label: draft.label, sortOrder: draft.sortOrder, isActive: true,
    });
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async update(id: string, draft: Partial<BusinessTypeDraft>): Promise<BusinessTypeRef> {
    const d = await this.http.patch<BusinessTypeDto>(`/admin/business-types/${id}`, draft);
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async toggleActive(id: string, currentIsActive: boolean): Promise<BusinessTypeRef> {
    const d = await this.http.patch<BusinessTypeDto>(`/admin/business-types/${id}`, { isActive: !currentIsActive });
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async remove(id: string): Promise<void> {
    await this.http.delete<void>(`/admin/business-types/${id}`);
  }
}

export class HttpOfferCategoryRepository implements OfferCategoryRepository {
  constructor(private readonly http: HttpClient) {}

  async list(): Promise<OfferCategoryRef[]> {
    const dto = await this.http.get<OfferCategoryDto[]>('/admin/offer-categories');
    return dto.map((d) => ({ id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive }));
  }

  async create(draft: OfferCategoryDraft): Promise<OfferCategoryRef> {
    const d = await this.http.post<OfferCategoryDto>('/admin/offer-categories', {
      slug: draft.slug, label: draft.label, sortOrder: draft.sortOrder, isActive: true,
    });
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async update(id: string, draft: Partial<OfferCategoryDraft>): Promise<OfferCategoryRef> {
    const d = await this.http.patch<OfferCategoryDto>(`/admin/offer-categories/${id}`, draft);
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async toggleActive(id: string, currentIsActive: boolean): Promise<OfferCategoryRef> {
    const d = await this.http.patch<OfferCategoryDto>(`/admin/offer-categories/${id}`, { isActive: !currentIsActive });
    return { id: d.id, slug: d.slug, label: d.label, sortOrder: d.sortOrder, isActive: d.isActive };
  }

  async remove(id: string): Promise<void> {
    await this.http.delete<void>(`/admin/offer-categories/${id}`);
  }
}

export class HttpCityRepository implements CityRepository {
  constructor(private readonly http: HttpClient) {}
  async list(): Promise<City[]> {
    const dto = await this.http.get<CityDto[]>('/admin/cities');
    return dto.map(map.toCity);
  }
}

export class HttpAuditLogRepository implements AuditLogRepository {
  constructor(private readonly http: HttpClient) {}

  async search(query: AuditLogQuery): Promise<AuditLogPage> {
    const dto = await this.http.get<PageDto<AuditLogEntry>>('/admin/audit-logs', {
      query: {
        ...(query.entity ? { entity: query.entity } : {}),
        ...(query.entityId ? { entityId: query.entityId } : {}),
        ...(query.action ? { action: query.action } : {}),
        ...(query.actorType ? { actorType: query.actorType } : {}),
        ...(query.actorId ? { actorId: query.actorId } : {}),
        ...(query.from ? { from: query.from } : {}),
        ...(query.to ? { to: query.to } : {}),
        ...(query.page ? { page: query.page } : {}),
        ...(query.pageSize ? { pageSize: query.pageSize } : {}),
      },
    });
    return {
      data: dto.data,
      total: dto.meta.total,
      page: dto.meta.page,
      pageSize: dto.meta.pageSize,
      pageCount: Math.max(1, dto.meta.pageCount),
    };
  }
}

export class HttpMobileConfigRepository implements MobileConfigRepository {
  constructor(private readonly http: HttpClient) {}

  async get(): Promise<MobileConfig> {
    const r = await this.http.get<MobileConfigApiResponse>('/admin/app-config');
    return {
      iosMinVersion: r.ios.minVersion,
      androidMinVersion: r.android.minVersion,
      iosForceUpdate: r.ios.forceUpdate,
      androidForceUpdate: r.android.forceUpdate,
    };
  }

  async save(config: MobileConfig): Promise<MobileConfig> {
    const r = await this.http.patch<MobileConfigApiResponse>('/admin/app-config', config);
    return {
      iosMinVersion: r.ios.minVersion,
      androidMinVersion: r.android.minVersion,
      iosForceUpdate: r.ios.forceUpdate,
      androidForceUpdate: r.android.forceUpdate,
    };
  }
}
